'use client';

import { type ComponentProps, forwardRef } from 'react';
import { m } from 'motion/react';

import { menuVariants } from '@shaojiang/lib/motion';

import * as Styled from '../styled/menu';
import {
  MotionPresenceProvider,
  onClosed,
  useMotionPresence,
  useMotionPresenceContext,
} from './presence';

// Everything not redefined below (Item, Trigger, Positioner…) is Park UI's as-is.
export * from '../styled/menu';

export type RootProps = Styled.RootProps;

/** Menu root that keeps the list mounted until its Motion exit animation is done. */
export const Root = (props: RootProps) => {
  const { open, defaultOpen, onOpenChange, ...rest } = props;
  const presence = useMotionPresence({ open, defaultOpen, onOpenChange });
  return (
    <MotionPresenceProvider value={presence}>
      <Styled.Root
        {...rest}
        open={presence.open}
        present={presence.present}
        onOpenChange={presence.onOpenChange}
      />
    </MotionPresenceProvider>
  );
};

export type ContentProps = ComponentProps<typeof Styled.Content>;

/** Menu list: pops out of its trigger (Ark sets the transform origin). */
export const Content = forwardRef<HTMLDivElement, ContentProps>(({ children, ...props }, ref) => {
  const { open, onExitComplete } = useMotionPresenceContext();
  return (
    <Styled.Content ref={ref} {...props} asChild>
      <m.div
        variants={menuVariants}
        initial='closed'
        animate={open ? 'open' : 'closed'}
        onAnimationComplete={onClosed(onExitComplete)}
      >
        {children}
      </m.div>
    </Styled.Content>
  );
});
Content.displayName = 'MenuContent';
